import React, { useEffect, useRef, useState } from 'react';
import { useListenerReady, ListenerNotReady } from '../../ListenerReadyGate/ListenerReadyGate';
import { rememberFreeOffer } from '../../../util/promoFirstFree';
import { FirstSessionPromise, BookingTrustLine } from '../../GrowthPromos/GrowthPromos';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { listingCardOneLiner, talkerBookingDisplayName } from '../../../util/listingCardCopy';
import { redirectGuestToSignupToBook } from '../../../util/bookingIntent';
import { isUserIdentityVerified, isListingAuthorVerified } from '../../../util/identity';
import {
  LISTENER_PRIMARY_CTA,
  LISTENER_STATUS,
  formatNextAvailableClock,
  resolveListenerAvailability,
} from '../../../util/listenerAvailability';
import AvailableNowBadge from '../../AvailableNowBadge/AvailableNowBadge';
import {
  SESSION_FORMAT_PRICES_SUBUNITS,
  SESSION_FORMAT_VARIANT_NAMES,
  offeredFormatsFromPublicData,
  buildInstantCheckoutValues,
} from '../../../util/sessionPricing';
import useReviewStats from '../../../util/useReviewStats';
import { emptyReviewStats } from '../../../util/listingReviews';
import { Avatar, PrimaryButton } from '../../../components';
import SuspendedNotice from '../../SuspendedNotice/SuspendedNotice';
import { useSafetySuspended } from '../../../util/safetySuspended';
import VerifiedBadge from '../../VerifiedBadge/VerifiedBadge';
import ListingRating from '../../ListingRating/ListingRating';
import IdentityRequiredNotice from '../../IdentityRequiredNotice/IdentityRequiredNotice';
import NoShowPolicyNotice from '../../NoShowPolicyNotice/NoShowPolicyNotice';
import TopicsTodayField, { bookingTopicExtras } from '../TopicsTodayField/TopicsTodayField';
import {
  useFreeForListener,
  FreeFormatPrice,
  FirstSessionFreePanelNote,
} from '../../FirstSessionFree/FirstSessionFree';

import ChargeReassurance from '../../ChargeReassurance/ChargeReassurance';
import { talkPriceSubunitsFor } from '../../../util/talkPrices';
import css from './TalkerBookingPanel.module.css';

const formatPrice = subunits => `$${Math.round(subunits / 100)}`;

const CTA_IDS = {
  chat: 'TalkerBookingPanel.ctaChat',
  voice: 'TalkerBookingPanel.ctaVoice',
  video: 'TalkerBookingPanel.ctaVideo',
};

const LightningIcon = () => (
  <svg className={css.modeIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M13.2 2.1 4.8 13.4c-.3.4 0 1 .5 1h6.1l-1.2 7.3c-.1.6.7 1 .1.4L19.2 10.6c.3-.4 0-1-.5-1h-6.1l1.2-7.1c.1-.6-.7-1-1.1-.4Z"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg className={css.modeIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3a1 1 0 0 1 1-1Zm12 8H5v10h14V10Z"
    />
  </svg>
);

const ChatIcon = () => (
  <svg className={css.formatIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
    />
  </svg>
);

const VoiceIcon = () => (
  <svg className={css.formatIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2a4 4 0 0 1 4 4v6a4 4 0 1 1-8 0V6a4 4 0 0 1 4-4Zm8 10a1 1 0 1 1 2 0 10 10 0 0 1-9 9.95V22h4a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2h4v-.05A10 10 0 0 1 2 12a1 1 0 1 1 2 0 8 8 0 1 0 16 0Z"
    />
  </svg>
);

const VideoIcon = () => (
  <svg className={css.formatIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M3 6h12a2 2 0 0 1 2 2v1.2l4.2-2.5A1 1 0 0 1 23 7.6v8.8a1 1 0 0 1-1.8.6L17 14.5V16a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
    />
  </svg>
);

const FORMAT_ICONS = { chat: ChatIcon, voice: VoiceIcon, video: VideoIcon };

const LATER_BOOKING_FORM_ID = 'OrderPanelBookingFixedDurationForm';

const readLaterCheckoutReady = root =>
  root?.querySelector?.('[data-book-checkout]')?.getAttribute('data-book-checkout') === 'ready';

const FormatCards = props => {
  const { formats, format, formatFieldName, formatError, onPick, free = false, reward = null, listing = null } = props;
  // Listener-set talk prices (TEST); free first session stays at the standard value.
  const priceFor = item => talkPriceSubunitsFor(listing, item) ?? SESSION_FORMAT_PRICES_SUBUNITS[item];
  return (
    <fieldset className={css.formats}>
      <legend className={css.legend}>
        <FormattedMessage id="SessionFormatPicker.label" />
      </legend>
      <div className={css.formatGrid} role="radiogroup">
        {formats.map(item => {
          const Icon = FORMAT_ICONS[item] || ChatIcon;
          return (
            <label
              key={item}
              className={classNames(css.formatCard, {
                [css.formatSelected]: format === item,
              })}
            >
              <span className={css.formatRadio} aria-hidden="true" />
              <input
                type="radio"
                name={formatFieldName}
                value={item}
                checked={format === item}
                onChange={() => onPick(item)}
              />
              <Icon />
              <span className={css.formatName}>{SESSION_FORMAT_VARIANT_NAMES[item]}</span>
              <span className={css.formatPrice}>
                {free ? (
                  <FreeFormatPrice
                    format={item}
                    reward={reward}
                    normalLabel={formatPrice(priceFor(item))}
                  />
                ) : priceFor(item) === 0 ? (
                  'Free'
                ) : (
                  formatPrice(priceFor(item))
                )}
              </span>
            </label>
          );
        })}
      </div>
      {formatError ? (
        <p className={css.error} role="alert">
          <FormattedMessage id="SessionFormatPicker.required" />
        </p>
      ) : null}
    </fieldset>
  );
};

export { talkerBookingDisplayName };

/**
 * Talker booking panel: Talk now vs Book for Later, then format or calendar.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const TalkerBookingPanel = props => {
  const {
    listing,
    author,
    currentUser,
    isOwnListing,
    online = false,
    startNowPaused = false,
    sessionBusy = false,
    nextAvailableAt = null,
    onSubmit,
    timeZone,
    laterForm = null,
    bookLater = false,
    onToggleBookLater,
    onLaterSubmit,
    hideIdentity = false,
    formatFieldName = 'talker-booking-format',
    reviewStats: reviewStatsProp,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const formats = offeredFormatsFromPublicData(listing?.attributes?.publicData);
  const [format, setFormat] = useState(null);
  const [formatError, setFormatError] = useState(false);
  const [topicsToday, setTopicsToday] = useState([]);
  const listenerReadyFlag = useListenerReady(listing?.id?.uuid);
  const [inProgress, setInProgress] = useState(false);
  const [laterCheckoutReady, setLaterCheckoutReady] = useState(false);
  const laterFormWrapRef = useRef(null);
  const hasLaterForm = !!laterForm;

  const displayName = talkerBookingDisplayName(listing, author || listing?.author);
  const tagline = listingCardOneLiner(listing);
  const listingId = listing?.id?.uuid;
  const statsMap = useReviewStats(reviewStatsProp || !listingId ? [] : [listingId]);
  const stats = reviewStatsProp || statsMap[listingId] || emptyReviewStats();
  const verifiedSelf = isUserIdentityVerified(currentUser);
  const verifiedListener = isListingAuthorVerified(listing);
  // Sam Oct 24 2026: buyers (talkers/learners) never need an ID check; 18+ confirm is enough.
  const buyerReady = verifiedSelf || require('../../../util/adultSelfAttest').isAdultSelfAttestedUser(currentUser);
  const listenerUserId = (author || listing?.author)?.id?.uuid || null;
  const firstFree = useFreeForListener(listenerUserId, currentUser || null);
  const accountSuspended = useSafetySuspended(!!currentUser && !isOwnListing);
  // Signed in, not ID-verified, first session still free (Sam, Oct 21 2026): book first,
  // then the quick safety step (18+ box; Talk Now needs nothing else) and back to checkout.
  const freeSetupPath = !!currentUser && !verifiedSelf && !!(firstFree.free || firstFree.needsVerify);
  useEffect(() => {
    rememberFreeOffer(listingId, !!(firstFree.free || freeSetupPath), firstFree.rewardSubunits || 3000);
  }, [listingId, firstFree.free, freeSetupPath, firstFree.rewardSubunits]);
  const availability = resolveListenerAvailability({
    listing,
    online,
    sessionBusy,
    startNowPaused,
    nextAvailableAt,
  });
  const talkNowEnabled = availability.talkNow;
  const later = bookLater || !talkNowEnabled;
  const nextClock = formatNextAvailableClock(
    availability.nextAvailableAt,
    timeZone || listing?.attributes?.availabilityPlan?.timezone
  );

  useEffect(() => {
    if (!hasLaterForm) {
      setLaterCheckoutReady(false);
      return undefined;
    }

    const readReady = () => {
      const wrap = laterFormWrapRef.current;
      const form =
        typeof document !== 'undefined' ? document.getElementById(LATER_BOOKING_FORM_ID) : null;
      setLaterCheckoutReady(readLaterCheckoutReady(wrap) || readLaterCheckoutReady(form));
    };

    readReady();
    const root =
      laterFormWrapRef.current || (typeof document !== 'undefined' ? document.body : null);
    if (!root || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    const observer = new MutationObserver(readReady);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-book-checkout'],
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [hasLaterForm]);

  if (!isOwnListing && listenerReadyFlag === false) {
    return (
      <div className={css.root} data-testid="talker-booking-panel" data-listener-ready="false">
        <ListenerNotReady />
      </div>
    );
  }
  if (isOwnListing) {
    return (
      <div className={css.root} data-testid="talker-booking-panel" data-own="true" data-own-online={availability.status === LISTENER_STATUS.AVAILABLE ? 'true' : 'false'}>
        <AvailableNowBadge status={availability.status} nextAvailableAt={availability.nextAvailableAt} />
        <p className={css.ownNote}>
          {availability.status === LISTENER_STATUS.AVAILABLE ? (
            <FormattedMessage id="StartNowPanel.ownListing" />
          ) : (
            'You’re offline for Start now. Turn on Available to Listen when you’re free; talkers can still book a later time.'
          )}
        </p>
        {availability.status === LISTENER_STATUS.AVAILABLE ? null : (
          <a href="/dashboard" className={css.ownNote} data-own-turn-on="true" style={{ fontWeight: 600, textDecoration: 'underline' }}>
            Turn on Available to Listen
          </a>
        )}
        {/* Sam Oct 25: clear owner view, no talker booking path on your own profile. */}
        <p className={css.ownNote} data-own-owner-view="true">This is your profile as others see it. You can’t book yourself.</p>
        <div data-own-links="true" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
          <a href="/my-profile" className={css.ownNote} style={{ fontWeight: 600, textDecoration: 'underline' }}>Edit my profile</a>
          <a href="/my-profile" className={css.ownNote} style={{ fontWeight: 600, textDecoration: 'underline' }}>Rates &amp; availability</a>
          {availability.status === LISTENER_STATUS.AVAILABLE ? (
            <a href="/dashboard" className={css.ownNote} style={{ fontWeight: 600, textDecoration: 'underline' }}>Go offline</a>
          ) : null}
        </div>
      </div>
    );
  }

  const pickMode = nextLater => {
    if (typeof onToggleBookLater === 'function') {
      onToggleBookLater(nextLater);
    }
  };

  // Sam 8:03 PM: eligible signed-in talkers choose first: free 15 min (18+ only) or paid (18+ + card, no ID).
  const [freeAsk, setFreeAsk] = useState(false);
  // Sam Nov 1 2026: agenda before Start now — one gentle nudge if no topic or note.
  const [topicNudge, setTopicNudge] = useState(false);
  const topicNudgedRef = useRef(false);
  const onStartNow = () => {
    if (!format) {
      setFormatError(true);
      return;
    }
    const extras = bookingTopicExtras(listing?.id?.uuid, topicsToday);
    if (!topicNudgedRef.current && !extras.topicPath && !extras.topicNote && !(extras.topicsToday || []).length) {
      topicNudgedRef.current = true;
      setTopicNudge(true);
      try {
        const el = document.querySelector('[data-topics-today="true"]');
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        // ignore
      }
      return;
    }
    setTopicNudge(false);
    if (currentUser && (firstFree.free || freeSetupPath)) {
      setFormatError(false);
      setFreeAsk(true);
      return;
    }
    runStartNow(null);
  };
  const runStartNow = useFree => {
    // Guests pick a format first (Sam, Oct 21 2026, TEST); the choice is saved and
    // carried through signup straight to checkout (handleSubmit → return path).
    if (!format) {
      setFormatError(true);
      return;
    }
    if (typeof onSubmit !== 'function') {
      return;
    }
    setFormatError(false);
    if (useFree === false) {
      rememberFreeOffer(listingId, false, 0);
      try {
        window.localStorage.removeItem('sl_free_now_intent');
      } catch (e) {
        // ignore
      }
    } else if (!currentUser || freeSetupPath || useFree === true) {
      rememberFreeOffer(listingId, true, 3000);
      try {
        window.localStorage.setItem('sl_free_now_intent', String(Date.now()));
      } catch (e) {
        // storage blocked: they can still finish with a card or ID
      }
    }
    setInProgress(true);
    onSubmit({
      ...buildInstantCheckoutValues({
        sessionFormat: format,
        timeZone: timeZone || listing?.attributes?.availabilityPlan?.timezone,
      }),
      ...bookingTopicExtras(listing?.id?.uuid, topicsToday),
      ...(useFree === false ? { declineFree: true } : {}),
      ...(useFree === true ? { firstSessionFree: true } : {}),
    });
    window.setTimeout(() => setInProgress(false), 1600);
  };

  const laterUsesNativeSubmit =
    later && hasLaterForm && typeof onLaterSubmit !== 'function';

  const onLaterCta = event => {
    if (!currentUser && !hasLaterForm) {
      event?.preventDefault?.();
      redirectGuestToSignupToBook(history, location);
      return;
    }
    if ((!currentUser && showFreeCta) || freeSetupPath) {
      rememberFreeOffer(listingId, true, 3000);
    }
    if (typeof onLaterSubmit === 'function') {
      event?.preventDefault?.();
      onLaterSubmit();
      return;
    }
    if (laterUsesNativeSubmit && laterCheckoutReady) {
      // Native type=submit form=… — do not requestSubmit() a display:none button (iOS ignores it).
      return;
    }
    event?.preventDefault?.();
    const dateField =
      document.getElementById(`${LATER_BOOKING_FORM_ID}.bookingStartDate`) ||
      document.getElementById('bookingStartDate');
    dateField?.focus();
    dateField?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const laterCtaId =
    availability.primaryCta === LISTENER_PRIMARY_CTA.BOOK_NEXT
      ? laterCheckoutReady
        ? 'TalkerBookingPanel.ctaContinueCheckout'
        : 'TalkerBookingPanel.ctaBookNext'
      : availability.primaryCta === LISTENER_PRIMARY_CTA.VIEW_AVAILABILITY
      ? laterCheckoutReady
        ? 'TalkerBookingPanel.ctaContinueCheckout'
        : 'TalkerBookingPanel.ctaViewAvailability'
      : laterCheckoutReady
      ? 'TalkerBookingPanel.ctaContinueCheckout'
      : 'TalkerBookingPanel.ctaLater';
  // Talker funnel (Oct 21 2026, TEST): "Start free" when the first 15 minutes
  // are free here (guest: the offer is on; signed in: eligible with this listener).
  const showFreeCta = !currentUser ? !!firstFree.live : !!firstFree.free || freeSetupPath;
  const freeReadyNow = !later && !!format;
  // Sam Nov 1 2026: instant path is Start free now / Start now (never Talk now).
  const freeCtaId = later ? 'TalkerBookingPanel.ctaStartFree' : 'TalkerBookingPanel.ctaStartFreeNow';
  const ctaId = !currentUser
    ? showFreeCta
      ? freeCtaId
      : 'StartNowPanel.logIn'
    : showFreeCta && (freeReadyNow || (later && laterCheckoutReady))
    ? freeCtaId
    : later
    ? laterCtaId
    : format
    ? 'TalkerBookingPanel.ctaStartNow'
    : 'TalkerBookingPanel.ctaPickFormat';

  return (
    <section
      className={css.root}
      data-testid="talker-booking-panel"
      data-online={online ? 'true' : 'false'}
      data-start-now-paused={startNowPaused ? 'true' : 'false'}
      data-session-busy={sessionBusy ? 'true' : 'false'}
      data-availability={availability.status}
      data-mode={later ? 'later' : 'now'}
    >
      {showFreeCta ? (
        <p className={css.freeBanner} data-testid="talker-free-banner">
          <span aria-hidden="true">🎁</span> Your first 15 minutes are free
        </p>
      ) : null}
      {hideIdentity ? null : (
        <header className={css.profile} data-testid="talker-booking-identity">
          <div className={css.photo}>
            <Avatar user={author || listing?.author} />
          </div>
          <div className={css.profileCopy}>
            <h2 className={css.displayName}>{displayName}</h2>
            {tagline ? <p className={css.tagline}>{tagline}</p> : null}
            <div className={css.meta}>
              <ListingRating average={stats.average} count={stats.count} hideEmpty />
              <VerifiedBadge user={author || listing?.author} />
              <AvailableNowBadge
                status={availability.status}
                nextAvailableAt={availability.nextAvailableAt}
                timeZone={timeZone || listing?.attributes?.availabilityPlan?.timezone}
              />
              {availability.status === LISTENER_STATUS.AVAILABLE ? (
                <span className={css.onlineNow} data-testid="online-now-pill">
                  <FormattedMessage id="TalkerBookingPanel.onlineNow" />
                </span>
              ) : null}
            </div>
          </div>
        </header>
      )}

      {availability.status === LISTENER_STATUS.IN_CONVERSATION ? (
        <p className={css.inConversation} data-testid="in-conversation-note">
          <FormattedMessage
            id={
              nextClock
                ? 'TalkerBookingPanel.inConversationNext'
                : 'TalkerBookingPanel.inConversation'
            }
            values={{ time: nextClock }}
          />
        </p>
      ) : null}

      {talkNowEnabled ? (
        <div className={css.step} data-testid="when-step">
          <p className={css.stepKicker}>
            <FormattedMessage id="TalkerBookingPanel.step1" />
          </p>
          <h3 className={css.stepTitle}>
            <FormattedMessage id="TalkerBookingPanel.stepWhen" values={{ name: displayName }} />
          </h3>
          <div className={css.modeRow} role="radiogroup" aria-label="When">
            <label
              className={classNames(css.modeCard, css.modeNow, {
                [css.modeSelected]: !later,
              })}
              data-testid="talk-now-card"
            >
              <input
                type="radio"
                name="talker-booking-when"
                value="now"
                checked={!later}
                onChange={() => pickMode(false)}
              />
              <LightningIcon />
              <span className={css.modeTitle}>
                <FormattedMessage id="TalkerBookingPanel.talkNowTitle" />
              </span>
              <span className={css.modeLede}>
                <FormattedMessage
                  id="TalkerBookingPanel.talkNowLede"
                  values={{ name: displayName }}
                />
              </span>
            </label>
            <label
              className={classNames(css.modeCard, css.modeLater, {
                [css.modeSelected]: later,
              })}
              data-testid="book-later-card"
            >
              <input
                type="radio"
                name="talker-booking-when"
                value="later"
                checked={later}
                onChange={() => pickMode(true)}
              />
              <CalendarIcon />
              <span className={css.modeTitle}>
                <FormattedMessage id="TalkerBookingPanel.bookLaterTitle" />
              </span>
              <span className={css.modeLede}>
                <FormattedMessage id="TalkerBookingPanel.bookLaterLede" />
              </span>
            </label>
          </div>
        </div>
      ) : null}

      {!verifiedListener ? <IdentityRequiredNotice context="listing" /> : null}
      {currentUser && !buyerReady && !freeSetupPath ? <IdentityRequiredNotice context="book" /> : null}
      {freeAsk ? (
        <div className={css.freeAskBackdrop} role="dialog" aria-modal="true" aria-labelledby="sl-free-ask-title" data-free-ask="true">
          <div className={css.freeAskBox}>
            <h2 id="sl-free-ask-title" className={css.freeAskTitle}>Would you like to use your free 15-minute session?</h2>
            <p className={css.freeAskText}>Yes needs only a quick 18+ confirm. No books a paid session (18+ and a card, no ID).</p>
            <button type="button" className={css.freeAskYes} data-free-ask-yes="true" onClick={() => { setFreeAsk(false); runStartNow(true); }}>Yes, use my free session</button>
            <button type="button" className={css.freeAskNo} data-free-ask-no="true" onClick={() => { setFreeAsk(false); runStartNow(false); }}>No, book a paid session</button>
          </div>
        </div>
      ) : null}

      {later ? (
        <div className={css.step} data-testid="later-step">
          <p className={css.stepKicker}>
            <FormattedMessage
              id={talkNowEnabled ? 'TalkerBookingPanel.step2' : 'TalkerBookingPanel.step1'}
            />
          </p>
          <h3 className={css.stepTitle}>
            <FormattedMessage
              id={laterForm ? 'TalkerBookingPanel.stepWhenLater' : 'TalkerBookingPanel.stepFormat'}
            />
          </h3>
          {!laterForm ? (
            <FormatCards
          listing={listing}
              free={firstFree.free}
              reward={firstFree.reward}
              formats={formats}
              format={format}
              formatFieldName={formatFieldName}
              formatError={formatError}
              onPick={item => {
                require('../../../util/bookingFunnel').trackBookingStep('format_pick', { format: item });
                setFormat(item);
                setFormatError(false);
              }}
            />
          ) : null}
          {laterForm ? (
            <div className={css.laterForm} ref={laterFormWrapRef}>
              {laterForm}
            </div>
          ) : null}
        </div>
      ) : (
        <div className={css.step} data-testid="now-step">
          <p className={css.stepKicker}>
            <FormattedMessage id="TalkerBookingPanel.step2" />
          </p>
          <h3 className={css.stepTitle}>
            <FormattedMessage id="TalkerBookingPanel.stepFormat" />
          </h3>
          {topicNudge ? (
            <div role="status" data-topic-nudge="true" style={{ background: '#fff4f8', border: '1px solid #f6c3d6', borderRadius: 12, padding: '10px 12px', margin: '0 0 10px', color: '#0b2350' }}>
              <strong>What would you like to talk about?</strong>
              <div style={{ fontSize: 14, marginTop: 2 }}>Pick a topic or jot a quick note below so {displayName} knows your agenda. Or tap Start now again to skip.</div>
            </div>
          ) : null}
          <TopicsTodayField listing={listing} selected={topicsToday} onChange={setTopicsToday} />
          <FormatCards
          listing={listing}
            free={firstFree.free}
            reward={firstFree.reward}
            formats={formats}
            format={format}
            formatFieldName={formatFieldName}
            formatError={formatError}
            onPick={item => {
              require('../../../util/bookingFunnel').trackBookingStep('format_pick', { format: item });
              setFormat(item);
              setFormatError(false);
            }}
          />
        </div>
      )}

      {currentUser ? (
        <FirstSessionFreePanelNote
          free={firstFree.free}
          listenerFull={firstFree.listenerFull}
          needsVerify={firstFree.needsVerify}
          reward={firstFree.reward}
        />
      ) : null}

      <BookingTrustLine />
      <NoShowPolicyNotice startNow={!later} role="talker" />

      {accountSuspended ? <SuspendedNotice compact /> : null}

      {/* A free first session costs $0, so skip "charged after your session". */}
      {firstFree?.free || showFreeCta ? null : <ChargeReassurance className={css.chargeLine} startNow={!later} />}
      {firstFree?.free || showFreeCta ? null : <FirstSessionPromise variant="charge" />}

      <PrimaryButton
        type={laterUsesNativeSubmit && laterCheckoutReady ? 'submit' : 'button'}
        form={laterUsesNativeSubmit ? LATER_BOOKING_FORM_ID : undefined}
        className={classNames(css.cta, later ? css.ctaLater : css.ctaNow)}
        data-testid="start-now-submit"
        data-talk-now-cta={later ? undefined : 'green'}
        data-book-cta={later ? 'pink' : undefined}
        data-later-checkout={later ? (laterCheckoutReady ? 'ready' : 'choose') : undefined}
        inProgress={inProgress}
        disabled={!verifiedListener || accountSuspended}
        data-suspended={accountSuspended ? 'true' : undefined}
        onClick={later ? onLaterCta : onStartNow}
      >
        <FormattedMessage key={ctaId} id={ctaId} values={{ name: displayName }} />
      </PrimaryButton>

      <ul className={css.trust} data-trust-list="true">
        <li>
          <span className={css.trustTitle}>
            <FormattedMessage id="TalkerBookingPanel.trustSecureTitle" />
          </span>
          <span className={css.trustBody}>
            <FormattedMessage id="TalkerBookingPanel.trustSecureBody" />
          </span>
        </li>
        {/* "Won't be charged now" moved above the button (ChargeReassurance, Oct 19 2026). */}
        <li data-trust-verify="true">
          <span className={css.trustTitle}>
            <FormattedMessage id="TalkerBookingPanel.trustVerifyTitle" />
          </span>
          <span className={css.trustBody}>
            <FormattedMessage id="TalkerBookingPanel.trustVerifyBody" />
          </span>
        </li>
      </ul>
    </section>
  );
};

export default TalkerBookingPanel;
